import { ExpandLess, ExpandMore } from '@mui/icons-material'
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural'
import {
  Collapse,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
} from '@mui/material'
import { useRouter } from 'next/router'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import type { TtoggleDrawerOnMobile } from '../../../containers/Sidebar/Sidebar'
import { useStyles } from '../../../containers/Sidebar/Sidebar.styles'
import AvatarBoCan from '../AvatarBoCan'
import { AVATAR_BO_PERMISSIONS } from '../shared/permissions'
import MenuCatalogue from './MenuCatalogue'
import MenuImageSizes from './MenuImageSizes'
import MenuReviewPublish from './MenuReviewPublish'
import MenuTiers from './MenuTiers'
import { CATALOGUE_PATH } from './paths'

export interface AvatarStudioSidebarSubMenuProps {
  permissions?: string[]
  toggleDrawerOnMobile: TtoggleDrawerOnMobile
  openDrawer?: boolean
  hoverDrawer?: boolean
}

const PUBLISH_SCREEN = [
  AVATAR_BO_PERMISSIONS.versionApprove,
  AVATAR_BO_PERMISSIONS.versionPublish,
]

export default function AvatarStudioSidebarSubMenu({
  toggleDrawerOnMobile,
  openDrawer,
  hoverDrawer,
}: AvatarStudioSidebarSubMenuProps) {
  const { formatMessage } = useIntl()
  const { pathname } = useRouter()
  const classes = useStyles({ openDrawer, hoverDrawer })
  const [open, setOpen] = useState(pathname.includes(CATALOGUE_PATH))

  const expanded = openDrawer || hoverDrawer
  const nested = expanded ? classes.nested : classes.nestedClosed
  const title = formatMessage({ id: 'avatarStudio.sidebar.title' })

  return (
    <AvatarBoCan
      // The union: somebody who can only approve still has to reach the studio.
      anyOf={[AVATAR_BO_PERMISSIONS.catalogueWrite, ...PUBLISH_SCREEN]}
      yes={() => (
        <MenuList
          aria-labelledby="nested-list-subheader"
          className={classes.root}
        >
          <MenuItem
            onClick={() => setOpen(!open)}
            className={classes.menuItem}
            // Named either way: on a collapsed rail the label is hidden and the icon alone
            // would leave the item nameless. Same words, so nothing is overridden.
            aria-label={title}
            aria-expanded={open}
            // Only when collapsed: with the children visible, they carry the selection.
            selected={!expanded && pathname.startsWith(CATALOGUE_PATH)}
            data-testid="avatar-studio-menu"
          >
            <ListItemIcon>
              <FaceRetouchingNaturalIcon />
            </ListItemIcon>
            <ListItemText primary={title} hidden={!expanded} />
            {open ? (
              <ExpandLess className={classes.expandIcon} />
            ) : (
              <ExpandMore className={classes.expandIcon} />
            )}
          </MenuItem>
          <Collapse
            in={open}
            timeout="auto"
            unmountOnExit={true}
            hidden={!expanded}
          >
            <AvatarBoCan
              perform={AVATAR_BO_PERMISSIONS.catalogueWrite}
              yes={() => (
                <MenuList component="div" disablePadding={true}>
                  <MenuCatalogue
                    pathname={pathname}
                    nested={nested}
                    toggleDrawerOnMobile={toggleDrawerOnMobile}
                  />
                  <MenuTiers
                    pathname={pathname}
                    nested={nested}
                    toggleDrawerOnMobile={toggleDrawerOnMobile}
                  />
                  <MenuImageSizes
                    pathname={pathname}
                    nested={nested}
                    toggleDrawerOnMobile={toggleDrawerOnMobile}
                  />
                </MenuList>
              )}
            />
            <AvatarBoCan
              anyOf={PUBLISH_SCREEN}
              yes={() => (
                <MenuList component="div" disablePadding={true}>
                  <MenuReviewPublish
                    pathname={pathname}
                    nested={nested}
                    toggleDrawerOnMobile={toggleDrawerOnMobile}
                  />
                </MenuList>
              )}
            />
          </Collapse>
        </MenuList>
      )}
    />
  )
}
